import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { Select } from "components/select/index";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faClose, faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
  IWebsiteBuilderListSettings,
  TWebsiteBuilderListType,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderList";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import {
  IWebsiteBuilderSelectOption,
  renderWebsiteBuilderSelectOptions,
} from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface IListSettingsProps {
  id: string;
  settings: IWebsiteBuilderListSettings;
  closeSettings: () => void;
}

export function ListSettings(props: IListSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderListSettings>(() => cloneDeep(settings));

  const typeOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderListType>[] = useMemo(() => {
    return [
      {
        label: t("Unordered"),
        value: "unordered",
      },
      {
        label: t("Ordered"),
        value: "ordered",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderListSettings, nextSettings: IWebsiteBuilderListSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "list",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "list",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleUpdateItem(value: string, index: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      items: [...prev.items.slice(0, index), value, ...prev.items.slice(index + 1)],
    }));
  }

  function handleRemoveItem(index: number) {
    handleUpdateState(prev => ({
      ...prev,
      items: [...prev.items.slice(0, index), ...prev.items.slice(index + 1)],
    }));
  }

  function handleMoveItem(index: number, shift: -1 | 1) {
    const nextIndex = index + shift;

    if (nextIndex < 0 || nextIndex >= updatedSettings.items.length) {
      return;
    }

    handleUpdateState(prev => {
      const items = [...prev.items];
      const temp = items[index];

      items[index] = items[nextIndex];
      items[nextIndex] = temp;

      return {
        ...prev,
        items,
      };
    });
  }

  function handleAddItem() {
    handleUpdateState(prev => ({
      ...prev,
      items: [...prev.items, ""],
    }));
  }

  function renderTypeSection() {
    return (
      <>
        <Select
          label={t("Type")}
          defaultValue={updatedSettings.type}
          onChange={(type: TWebsiteBuilderListType) =>
            handleUpdateState(prev => ({
              ...prev,
              type,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(typeOptions)}
        </Select>
      </>
    );
  }

  function renderItemsSection() {
    return (
      <>
        <div className="website-builder-list-settings-items-title">{t("Items")}</div>
        {updatedSettings.items.map((item, index) => (
          <div key={index} className="website-builder-list-settings-item">
            <Input
              labelHidden
              placeholder={`${t("Item")} ${index + 1}`}
              value={item}
              onChange={e => handleUpdateItem(e.target.value, index)}
            />
            <button
              className="list-settings-item-shift"
              type="button"
              onClick={() => handleMoveItem(index, -1)}
              disabled={index === 0}
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button
              className="list-settings-item-shift"
              type="button"
              onClick={() => handleMoveItem(index, 1)}
              disabled={index === updatedSettings.items.length - 1}
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
            <FontAwesomeIcon
              className="list-settings-item-remove"
              icon={faClose}
              onClick={() => handleRemoveItem(index)}
            />
          </div>
        ))}
        <Button
          className="website-builder-list-settings-add-item"
          type="secondary"
          block
          size="small"
          onClick={handleAddItem}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t("Add item")}
        </Button>
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("List settings")}
      closePanel={closeSettings}
      sections={[renderTypeSection(), renderItemsSection()]}
    />
  );
}
